import React, { useState } from 'react';
import { OccupationHierarchy } from './types';

interface HierarchyItemProps {
  occupation: OccupationHierarchy;
  level: number;
  onUpdateOccupation: (occupationId: number, name: string, display_priority?: number) => Promise<void>;
  onTogglePublished: (occupationId: number) => Promise<void>;
  [key: string]: any; // ドラッグ属性用
}

const HierarchyItem: React.FC<HierarchyItemProps> = ({
  occupation,
  level,
  onUpdateOccupation,
  onTogglePublished,
  ...dragProps
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editName, setEditName] = useState(occupation.name);
  const [editDisplayPriority, setEditDisplayPriority] = useState(occupation.display_priority);
  const [isSaving, setIsSaving] = useState(false);

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!editName.trim()) {
      alert('職種名を入力してください');
      return;
    }

    const nameChanged = editName.trim() !== occupation.name;
    const displayPriorityChanged = editDisplayPriority !== occupation.display_priority;

    if (!nameChanged && !displayPriorityChanged) {
      setIsEditing(false);
      return;
    }

    setIsSaving(true);
    try {
      await onUpdateOccupation(occupation.id, editName.trim(), displayPriorityChanged ? editDisplayPriority : undefined);
      setIsEditing(false);
    } catch (error) {
      console.error('Error updating occupation:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setEditName(occupation.name);
    setEditDisplayPriority(occupation.display_priority);
    setIsEditing(false);
  };

  if (isEditing) {
    return (
      <form
        className="d-flex align-items-center flex-grow-1 gap-2"
        onSubmit={handleSave}
        onClick={(e) => e.stopPropagation()}
      >
        <i className="bi bi-grip-vertical me-2 text-muted"></i>
        <input
          type="text"
          className="form-control form-control-sm"
          style={{ flex: '1 1 auto', minWidth: '150px' }}
          value={editName}
          onChange={(e) => setEditName(e.target.value)}
          autoFocus
          disabled={isSaving}
          placeholder="職種名"
        />
        <input
          type="number"
          className="form-control form-control-sm"
          style={{ width: '80px' }}
          value={editDisplayPriority}
          onChange={(e) => setEditDisplayPriority(parseInt(e.target.value, 10) || 0)}
          min="0"
          disabled={isSaving}
          placeholder="優先度"
          title="ディスプレイプライオリティ（大きい値が優先度が高い）"
        />
        <button
          type="submit"
          className="btn btn-sm btn-success"
          disabled={isSaving || !editName.trim()}
          title="保存"
        >
          <i className="bi bi-check"></i>
        </button>
        <button
          type="button"
          className="btn btn-sm btn-secondary"
          onClick={handleCancel}
          disabled={isSaving}
          title="キャンセル"
        >
          <i className="bi bi-x"></i>
        </button>
      </form>
    );
  }

  return (
    <div
      className="d-flex align-items-center flex-grow-1 cursor-move"
      {...dragProps}
      style={{ cursor: 'grab' }}
    >
      <i className="bi bi-grip-vertical me-2 text-muted"></i>
      <span 
        className="fw-semibold me-2"
        onDoubleClick={(e) => {
          e.stopPropagation();
          setIsEditing(true);
        }}
        style={{ cursor: 'text' }}
        title="ダブルクリックで編集"
      >
        {occupation.name}
      </span>
      <button
        type="button"
        className="btn btn-sm btn-link p-0 me-2"
        onClick={(e) => {
          e.stopPropagation();
          setIsEditing(true);
        }}
        title="編集"
        style={{ minWidth: '20px' }}
      >
        <i className="bi bi-pencil text-muted"></i>
      </button>
      {occupation.children.length > 0 && (
        <span className="badge bg-info ms-2">
          {occupation.children.length}件
        </span>
      )}
      <span className="badge bg-secondary ms-2" title="ディスプレイプライオリティ（大きい値が優先度が高い）">
        P{occupation.display_priority}
      </span>
      <span className="badge bg-secondary ms-2">L{level}</span>
      <div className="form-check form-switch ms-3" onClick={(e) => e.stopPropagation()}>
        <input
          className="form-check-input"
          type="checkbox"
          role="switch"
          id={`published-toggle-${occupation.id}`}
          checked={occupation.published || false}
          onChange={(e) => {
            e.stopPropagation();
            onTogglePublished(occupation.id);
          }}
          title={occupation.published ? '公開中' : '非公開'}
        />
        <label className="form-check-label" htmlFor={`published-toggle-${occupation.id}`}>
          <span className={`published-status-${occupation.id}`}>
            {occupation.published ? (
              <span className="badge bg-success">公開</span>
            ) : (
              <span className="badge bg-secondary">非公開</span>
            )}
          </span>
        </label>
      </div>
    </div>
  );
};

export default HierarchyItem;

